import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortfolio } from '@/services/portfolio/PortfolioService';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { logger } from '@/services/LoggingService';
import { usePortfolioManagement } from '@/hooks/usePortfolioManagement';
import type { WealthicaPosition } from '@/providers/wealthica2/types';
import type { GenericPosition } from './dialog/types';
export type { GenericPosition } from './dialog/types';
import { addPositionToPortfolio as addPositionToPortfolioHelper } from './dialog/addPositionToPortfolio';
import { useAuthStore } from '@/stores/authStore';
import { formatCurrency } from '@/lib/portfolioCalculations';

type MinimalPortfolio = { id?: string; portfolio_type?: string };

export interface UseAddPositionsDialogParams {
  isOpen?: boolean;
  open?: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  positions: (WealthicaPosition | GenericPosition)[];
  onSuccess?: () => void;
  navigateOnZeroAdded?: boolean;
}

export const useAddPositionsDialog = ({
  isOpen,
  open,
  onClose,
  onOpenChange,
  positions,
  onSuccess,
  navigateOnZeroAdded,
}: UseAddPositionsDialogParams) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Derived open state for compatibility
  const isDialogOpen = useMemo(() => open ?? isOpen ?? false, [open, isOpen]);

  // UI state
  const [isComboOpen, setIsComboOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [processingPositions, setProcessingPositions] = useState<Record<string, boolean>>({});
  const [currentPositionIndex, setCurrentPositionIndex] = useState<number>(0);
  const [successCount, setSuccessCount] = useState<number>(0);
  const [failedCount, setFailedCount] = useState<number>(0);
  const [selectedPositions, setSelectedPositions] = useState<(WealthicaPosition | GenericPosition)[]>(positions);
  const [showAllPositions, setShowAllPositions] = useState<boolean>(false);

  // Portfolio management hook
  const {
    isCreatingNew,
    newPortfolioName,
    portfolioType,
    selectedPortfolioId,
    portfolios,
    isLoading,
    errorMessage,
    setIsCreatingNew,
    setNewPortfolioName,
    setPortfolioType,
    setSelectedPortfolioId,
    fetchPortfolios,
    handleClose: closePortfolioDialog,
  } = usePortfolioManagement({ onSuccess, onClose });

  // Keep positions in sync
  useEffect(() => {
    setSelectedPositions(positions);
  }, [positions]);

  // Handle open/close changes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onClose?.();
      onOpenChange?.(newOpen);
    }
  };

  // NOTE ABOUT DEPENDENCIES
  // This effect initializes dialog state on open. We intentionally avoid adding
  // fetchPortfolios (unstable identity) or setPortfolioType (stable but not required)
  // to the dependency array because it would re-trigger on every render and cause
  // repeated portfolio refetches and type resets. We only set a default type if none
  // is chosen yet and fetch portfolios exactly once per open.
  useEffect(() => {
    if (!isDialogOpen) {
      // Reset transient state when dialog closes
      setProcessingPositions({});
      setCurrentPositionIndex(0);
      setSuccessCount(0);
      setFailedCount(0);
      return;
    }

    // Dialog opened: initialize once per open
    // Only set default when no type has been chosen yet to avoid clobbering user selection (e.g., 'client')
    if (!portfolioType) {
      setPortfolioType('personal');
    }

    setSuccessCount(0);
    setFailedCount(0);
    setCurrentPositionIndex(0);
    setProcessingPositions({});

    // Fetch portfolios once when dialog opens. Intentionally avoid including fetchPortfolios
    // in the dependency array to prevent infinite loops caused by unstable function identity.
    void fetchPortfolios();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen]);

  const handleClose = () => {
    closePortfolioDialog();
    onClose?.();
    onOpenChange?.(false);
  };

  const removePosition = (positionId: string) => {
    setSelectedPositions((prev) => (prev || []).filter((p) => p.id !== positionId));
  };

  // mapPositionToAssetType moved to ./dialog/mapPositionToAssetType

  // addPositionToPortfolio extracted to ./dialog/addPositionToPortfolio

  const handleAddPositions = async () => {
    logger.info('AddPositionsToPortfolioDialog', 'Starting handleAddPositions', {
      selectedPositionsCount: selectedPositions?.length || 0,
      isCreatingNew,
      newPortfolioName,
      portfolioType,
      selectedPortfolioId
    });

    if ((selectedPositions?.length || 0) === 0) {
      logger.warn('AddPositionsToPortfolioDialog', 'No positions selected');
      toast.error('No positions selected to add');
      return;
    }

    setSuccessCount(0);
    setFailedCount(0);
    setCurrentPositionIndex(0);

    let localSuccessCount = 0;
    let localFailedCount = 0;

    // Prepare portfolio
    let targetPortfolioId: string;
    let createdNewPortfolio = false;

    if (isCreatingNew) {
      logger.info('AddPositionsToPortfolioDialog', 'Creating new portfolio', { newPortfolioName, portfolioType });
      if (!newPortfolioName?.trim()) {
        logger.warn('AddPositionsToPortfolioDialog', 'Portfolio name is required');
        toast.error('Portfolio name is required');
        return;
      }
      try {
        const userId = useAuthStore.getState().user?.id;
        if (!userId) {
          logger.error('AddPositionsToPortfolioDialog', 'User not authenticated');
          toast.error('Authentication error');
          return;
        }
        logger.info('AddPositionsToPortfolioDialog', 'User authenticated', { userId });
        logger.debug('AddPositionsToPortfolioDialog', 'Creating new portfolio', { name: newPortfolioName });
        const newId = await createPortfolio({ userId, name: newPortfolioName.trim(), portfolioType: portfolioType || 'personal' });
        targetPortfolioId = newId;
        setIsCreatingNew(false);
        setSelectedPortfolioId(targetPortfolioId);
        fetchPortfolios();
        createdNewPortfolio = true;
      } catch (error) {
        logger.error('AddPositionsToPortfolioDialog', 'Error creating portfolio', { error });
        toast.error('Failed to create portfolio');
        return;
      }
    } else {
      logger.info('AddPositionsToPortfolioDialog', 'Using existing portfolio', { selectedPortfolioId });
      if (!selectedPortfolioId) {
        logger.warn('AddPositionsToPortfolioDialog', 'No portfolio selected');
        toast.error('No portfolio selected');
        return;
      }
      targetPortfolioId = selectedPortfolioId;
    }

    logger.info('AddPositionsToPortfolioDialog', 'Target portfolio determined', { targetPortfolioId });

    // Pre-validation & auto-grow
    try {
      const { prevalidateAdditions } = await import('./dialog/prevalidateAdditions');
      const { additionsDollarTotal } = await prevalidateAdditions(targetPortfolioId, selectedPositions);
      if (additionsDollarTotal > 0) {
        const { validateAndAutoGrowPortfolio } = await import('@/lib/portfolioValidation');
        const result = await validateAndAutoGrowPortfolio(targetPortfolioId, Math.round(additionsDollarTotal * 100) / 100);
        if (!result.allowed) {
          toast.error(result.message || 'Cannot add positions: portfolio size would be exceeded.');
          return;
        }
        if (result.newSizeApplied) {
          toast.info(`Portfolio size auto-expanded to ${formatCurrency(result.newSizeApplied)}.`);
        }
      }
    } catch (e) {
      logger.warn('AddPositionsToPortfolioDialog', 'Validation step encountered an issue', { e });
    }

    for (let i = 0; i < (selectedPositions?.length || 0); i++) {
      const position = selectedPositions[i];
      setCurrentPositionIndex(i);
      setProcessingPositions((prev) => ({ ...prev, [position.id]: true }));
      const success = await addPositionToPortfolioHelper(position, targetPortfolioId);
      if (success) {
        localSuccessCount++;
        setSuccessCount((prev) => prev + 1);
      } else {
        localFailedCount++;
        setFailedCount((prev) => prev + 1);
      }
      setProcessingPositions((prev) => ({ ...prev, [position.id]: false }));
    }

    if (localSuccessCount > 0 || createdNewPortfolio) {
      void queryClient.invalidateQueries({ queryKey: ['portfolio', targetPortfolioId] });
      void queryClient.invalidateQueries({ queryKey: ['portfolio_allocations', targetPortfolioId] });
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
    }

    if (onSuccess && (localSuccessCount > 0 || createdNewPortfolio)) {
      onSuccess();
    }

    logger.info('AddPositionsToPortfolioDialog', 'Build portfolio process complete', {
      targetPortfolioId,
      createdNewPortfolio,
      localSuccessCount,
      localFailedCount,
    });

    const shouldNavigate = localSuccessCount > 0 || createdNewPortfolio || (!!navigateOnZeroAdded && localSuccessCount === 0 && !createdNewPortfolio);
    if (shouldNavigate) {
      handleClose();
      setTimeout(() => navigate(`/portfolios/build/${targetPortfolioId}`), 100);
    } else {
      if (localSuccessCount > 0) {
        toast.success(`Added ${localSuccessCount} positions to portfolio successfully${localFailedCount > 0 ? `, ${localFailedCount} skipped` : ''}`);
      } else if (localFailedCount > 0) {
        const message = localFailedCount === (selectedPositions?.length || 0)
          ? `All ${localFailedCount} position${localFailedCount > 1 ? 's' : ''} already existed in portfolio`
          : `${localFailedCount} position${localFailedCount > 1 ? 's' : ''} already existed in portfolio`;
        toast.info(message);
      }
      setTimeout(() => handleClose(), 1000);
    }
  };

  const hasPositions = !!positions && positions.length > 0;
  const selectedIsModel = (portfolios as MinimalPortfolio[] | undefined || []).some((p) => p?.id === selectedPortfolioId && p?.portfolio_type === 'model');
  const safeSelectedPortfolioId = selectedIsModel ? '' : (selectedPortfolioId || '');

  return {
    // state
    isDialogOpen,
    isComboOpen,
    setIsComboOpen,
    isTypeOpen,
    setIsTypeOpen,
    processingPositions,
    currentPositionIndex,
    successCount,
    failedCount,
    selectedPositions,
    setSelectedPositions,
    showAllPositions,
    setShowAllPositions,

    // portfolio management
    isCreatingNew,
    newPortfolioName,
    portfolioType,
    selectedPortfolioId,
    portfolios,
    isLoading,
    errorMessage,
    setIsCreatingNew,
    setNewPortfolioName,
    setPortfolioType,
    setSelectedPortfolioId,
    fetchPortfolios,

    // handlers
    handleOpenChange,
    handleClose,
    removePosition,
    handleAddPositions,

    // derived
    hasPositions,
    selectedIsModel,
    safeSelectedPortfolioId,
  };
};
